<div class="component-wrapper">
  <div class="content-wrapper">
    <h1>Hypothesen testen</h1>
    <p>
      Wir wollen euch an dieser Stelle ein paar verschiedene Strategien für das
      Testen empirischer Hypothesen vorstellen. Zum einen die klassische
      Statistik und zum anderen Cohens Effektstärken und (wieder) moderne
      Bayssche Statistik.
    </p>
    <p>
      Dabei starten wir mit fundamentalen Begriffen wie P-Wert,
      Signifikanzniveau, Fehler 1.- und Fehler 2. Art. Denn in sehr vielen
      wissenschaftlichen Projekten haben wir die Erfahrung gemacht, dass viele
      gar nicht genau wissen, was Signifikanz eigentlich bedeutet und wofür der
      P-Wert im Grunde steht. Die beliebtesten Ansätze sind dabei, dass
      Signifikanz mit „nicht zufällig“ „bedeutsam“ oder „wichtig“ erklärt wird.
      Das ist so nicht richtig, wenn auch die eigentliche Definition ist nicht
      besonders schwierig ist.
    </p>
    <h2 id="p-wert">Was heißt „Signifikanz“?</h2>
    <p>
      Um Signifikanz zu verstehen, müssen wir uns anschauen, was es mit dem
      Fehler 1. Und 2. Art auf sich hat. Hier nehmen wir einmal das Beispiel
      eines Corona-Schnelltests. Aktuell (Stand: April, 2021) ein emotional sehr
      aufgeladenes Thema.
    </p>
    <p>
      Wir gehen also einmal davon aus, dass du typische Krankheitssymptome
      (Fieber, Verlust von Geschmack, Husten etc.) hast und in die nächste
      Testambulanz gehst.
    </p>
    <h2>Fehler 1. Art & Fehler 2. Art</h2>
    <p>
      Ein Test kann immer fehleranfällig sein und aus der Kombination der
      möglichen Outcomes und Fehler ergibt sich folgendes Schaubild:
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/pwert/p-wert-test-outcome.png"
        class="width-50"
        alt="p-wert testausgänge"
      />
    </div>
    <p>
      Ist ein Test in der Lage infizierte Personen als solche zu erkennen,
      spricht man von Sensitivität. Kann er nicht-Infizierte korrekt als negativ
      erkennen, von Spezifität.
    </p>
    <p>
      Ein Test kann aber falsche Ergebnisse liefern. Falsch positiv heißt dabei,
      dass du keine Infektion mit SARS-Covid-19 hast, der Test dich aber als
      infiziert einstuft. Falsch negativ heißt, du bist infiziert, der Test
      übersieht dich aber. Das Ganze ist in der Praxis sehr relevant, denn:
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/pwert/p-wert-test-outcome-praxisnah.png"
        class="width-50"
        alt="p-wert testausgänge praxisnah"
      />
    </div>
    <p>
      Entscheidend sind also die Fehler des Tests. Bist du nicht infiziert, der
      Test wird aber falsch positiv, ist dies sehr ärgerlich und du musst
      umsonst 2 Wochen in Quarantäne. Entscheidender und viel relevanter ist
      aber der falsch negative Test. Du bist infiziert und infizierst deine
      Mitmenschen, musst aber fälschlicherweise nicht Quarantäne. Falsch positiv
      nennt man Alpha-Fehler (α), falsch negativ den Beta-Fehler (β).
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/pwert/p-wert-test-outcome-alpha-beta.png"
        class="width-50"
        alt="p-wert testausgänge alpha beta bezeichnung"
      />
    </div>
    <p>
      Und hier kommt jetzt der P-Wert, auch bekannt als Signifikanzwert ins
      Spiel. Denn was wir hier mit Corona-Tests erklären, gilt vor allem für
      empirische Hypothesen und deren statistische Tests.
    </p>
    <p>
      Wenn du postulierst, dass es beispielsweise einen Unterscheid zwischen
      zwei Gruppen hinsichtlich eines Merkmals gibt (T-Test), kannst du dich
      dabei natürlich irren. Es kann sein, dass du einen Gruppenunterschied
      annimmst, der aber nicht da ist (falsch positiv bzw. α-Fehler bzw. Fehler
      1. Art). Hier müsste eigentlich deine Nullhypothese beibehalten werden.
      Das Gegenteil wäre der Fall, wenn du keinen Unterschied annimmst, jedoch
      einer besteht (falsch negativ bzw. β-Fehler bzw. Fehler 2. Art). Dies ist
      sehr ärgerlich, denn wir alle wollen ja signifikante Ergebnisse.
    </p>
    <h2>P-Wert</h2>
    <p>
      Was ist jetzt also der P-Wert. Der P-Wert ist die Wahrscheinlichkeit
      (Probability) einen α-Fehler zu begehen! Also von einem Effekt zu
      sprechen, obwohl eigentlich keiner da ist.
    </p>
    <p>
      Eine etwas sperrigere Erklärung ist, dass der P-wert die
      Wahrscheinlichkeit ist, unter der Annahme, dass die Nullhypothese nicht
      verworfen werden kann, ein bestimmtes Stichprobenergebnis zu beobachten.
    </p>
    <p>
      Beide laufen auf dasselbe hinaus. Ein kleiner P-Wert heißt also, eine
      geringe α -Fehlerwahrscheinlichkeit bzw. eine geringe Wahrscheinlichkeit
      einer Beobachtung unter der Annahme der Nullhypothese. Und daher hat
      beispielsweise das Signifikanzniveau-α seinen Namen.
    </p>
    <h2>Signifikanzniveau (auch Signifikanzniveau- α)</h2>
    <p>
      Hier haben sich in den letzten Jahrzehnten 3 Niveaus etabliert. 5%
      (vermutlich am häufigsten), 1% und 0.1%. Als Faktor also 0.05, 0.01 und
      0.001.
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/pwert/p-wert-iq-distribution.jpg"
        alt="p-wert I.Q. Distribution"
      />
    </div>
    <div class="img-text">
      Abbildung 1: Einseitiger Ablehnbereich von 5%, α=.05 (Krahl, 2021)
    </div>
    <p>
      In der Abbildung ist eine zufällige Normalverteilung des IQ dargestellt.
      Ein Signifikanzniveau von 5% wurde gewählt und der einseitige
      Ablehnbereich (der Nullhypothese) markiert. Würde beispielsweise der
      Mittelwert einer zweiten Verteilung in diesen Bereich fallen, wäre der
      Unterschied signifikant.
    </p>
    <p>
      Unterschreitet der P-Wert das Signifikanzniveau, spricht man also von
      Signifikanz. Und hier liegt nun aber die erste und eine der der größten
      Kritikpunkte am klassischen Testen. Woher kommen diese Signifikanzniveaus?
      Sie sind willkürlich gewählt und werden oft nach Belieben verschoben um am
      Ende doch noch signifikante Ergebnisse zu erhalten. Und wer sagt, dass ein
      5% Alpha-Fehler nicht viel zu hoch ist?
    </p>
    <h1>Kritik am klassischen Testen</h1>
    <p>
      Die Kritik am klassischen Testen ist so alt wie diese Methode an sich. Zum
      Teil ist sie berechtigt, zum Teil aber auch nicht. Wir wollen euch die
      Kritikpunkte einmal erklären, damit ihr Studienergebnisse besser und
      kritischer Interpretieren könnt.
    </p>
    <h2>Alpha-Kumulierung</h2>
    <p>
      Neben der Willkür in der Festlegung des Signifikanzniveaus spielt die
      α-Kumulierung eine sehr große Rolle. Angenommen du hast nun
      Erkältungssymptome und willst dich auf SARS-Covid-19 testen lassen. Wir
      gehen davon aus, dass wir eine 5% α-Fehlerwahrscheinlichkeit bei
      Corona-Tests beobachten können. Was würde passieren, wenn du einfach 100
      Tests machen würdest? Es würden wahrscheinlich 5 Tests falsch positiv
      werden! In 1 der 20 Fällen würdest du also angezeigt bekommen, dass du
      Corona hast, obwohl dies nicht der Fall ist. Das nennt sich
      Alpha-Kumulierung und entsteht durch multiples Testen. Je mehr Tests man
      macht, umso mehr Ergebnisse werden also zwangläufig signifikant, wenn man
      das Signifikanzniveau oder den P-Wert nicht korrigiert.
    </p>
    <p>
      Tatsächlich stellt dies ein massives Problem in der modernen
      wissenschaftlichen Welt dar. Gerade im humanwissenschaftlichen Bereich
      werden in wissenschaftlichen Arbeiten dutzende oder zum Teil hunderte
      statistische Tests durchgeführt. Publiziert werden am Ende aber oft nur
      die signifikanten Ergebnisse. Diese sind dann wahrscheinlich nur α
      -Fehler. Und diese können dann letztlich nicht mehr repliziert werden.
    </p>
    <p>
      Daher ist eine Anpassung der Signifikanzniveaus oder des P-Wertes
      erforderlich. Hier haben sich mittlerweile viel Verfahren etabliert, wie
      die Bonferroni-, Bonferroni-Holm-, Tukey T- oder Dunnet-Korrektur.
    </p>
    <h2>Einfluss der Stichprobengröße N auf den P-Wert</h2>
    <p>
      Ein weiteres Problem stellt der Einfluss der Stichprobengröße auf den
      P-Wert dar. In kleinen Stichproben brauchen wir schon starke Effekte,
      damit der Test signifikant wird. In sehr großen Stichproben hingegen
      werden minimale Effekte bereits hochsignifikant. Diese liegen zum Teil im
      Messfehlerbereich und werden dann dennoch publiziert.
    </p>
    <p>
      Um den Einfluss der Stichprobengröße auf den P-Wert zu versanschaulichen
      nehmen wir an dieser Stelle die Formel des einfachen T-Tests für
      unabhängige Stichproben.
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/pwert/t-test-unabhängige-stichproben.png"
        class="width-40"
        alt="t-test formel unabhängige stichproben"
      />
    </div>
    <p>
      Hier wird das Folgende deutlich: Je größer die Stichproben (n1, n2) sind,
      umso kleiner wird der Term im Divisor und umso höher wird automatisch der
      Quotient, in diesem Fall t. Bei gleichen Mittelwertunterschieden zwischen
      zwei Gruppe, erhält man also in Abhängigkeit der Stichprobengröße
      unterschiedliche P-Werte. Wenn die Stichprobe groß genug wird, werden im
      klassischen Testen sogar Tests mit randomisierten Daten signifikant. Wir
      haben das einmal in einer kleinen Studie mit R und eigenen Zufallsdaten
      dargestellt:
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/verhältnis-p-wert-und-stichprobengröße.png"
        alt="verhältnis von p-wert und stichprobengröße"
        class="width-60"
      />
    </div>
    <div class="img-text">
      Abbildung 2: Mittelwertsunterschied und P-Wert in Abhängigkeit der
      Stichprobengröße (Krahl, 2021)
    </div>
    <p>
      An dieser Stelle haben wir zwei normalverteilte Zufallsverteilungen
      gebildet. Im Randomisierungsprozess haben wir jeweils einen Mittelwert von
      100 und eine Standardabweichung von 15 festgelegt. Damit bilden die beiden
      zufälligen Verteilungen nun den normalverteilten IQ ab. Die erste
      Zufallsverteilung soll dabei der IQ aus dem Jahr 2000 mit einem Mittelwert
      von 101.254 und einer Standardabweichung von 14.776 darstellen. Die zweite
      zufällige Verteilung ist der hypothetische IQ aus dem Jahr 2020 mit einem
      Mittelwert von 101.121 und einer Standardabweichung von 14.089. Der
      Mittelwertsunterschied in unserem Beispiel beträgt also lediglich 0.133
      IQ-Punkte.
    </p>
    <p>
      Es wird vermutlich den meisten einleuchten, dass IQ-Punkt bzw. ein
      Unterschied von Standardabweichung gar nicht messbar ist und eher im
      Bereich eines Messfehlers liegt.
    </p>
    <p>
      Wenn wir nun diese zufälligen Verteiluingen mit den Größen N=100 mit einem
      T-Test für verbundene Stichproben testen, ergibt sich natürlich kein
      signifikanter Unterschied (t(99) = .059 , <b>p = .952</b>) Unser P-Wert
      ist weit entfernt von 0.05 (s. Abb. 2).
    </p>
    <p>
      Wenn wir aber im nächsten Schritt die Stichprobe um den Faktor 10
      vergrößern, sieht es anders aus. Die Daten bleiben dieselben. Der
      Mittelwertsunterschied zwischen beiden Verteilung bleibt gleich (s. Abb.2,
      Mean Diff). Deskriptiv ändert sich nichts außer der Stichprobengröße. Was
      sich aber ändert ist der P-wert. Im T-Test ist dieser nun leicht gefallen
      (t(999) = . 188, <b> p = . 850</b>).
    </p>
    <p>
      Bei einem N von 110000 unterschreitet er dann sogar das Signifikanzniveau,
      obwohl es sich um gleich randomisierte Zufallsdaten handelt (t(109999) =
      1.978, <b>p = . 047</b>; s. Abb. 2). Natürlich kann man jetzt
      argumentieren, dass eine sehr große Stichprobe eher robuste Ergebnisse
      liefert und der Effekt eher repräsentativ ist. Es ist zudem
      wahrscheinlicher, dass ein Effekt tatsächlich vorhanden ist. Problematisch
      wird diese Argumentation aber eben, wenn winzige Effekte, die auf Mess-
      oder Rundungsfehlern basieren, signifikant werden. In unserem Beispiel
      haben wir also einen signifikanten Unterschied zwischen zwei gleich
      randomisierten Verteilungen gefunden und würden mit hoher
      Wahrscheinlichkeit einen α-Fehler publizieren. Doch so etwas muss nicht
      passieren.
    </p>
    <h2 id="effektstaerken">
      Alternativen zum P-Wert - Der Bayes Faktor und die Effektstärke
    </h2>
    <p>
      An dieser Stelle wollen wir euch zwei Alternativen zum klassischen Testen
      vorstellen, die das Problem der α-Kumulierung und der Abhängigkeit von der
      Stichprobengröße zum Teil umgehen können.
    </p>
    <h2>Einfluss der Stichprobengröße N auf die Effektstärke</h2>
    <p>
      In Abbildung 3 haben wir den Einfluss der Stichprobengröße auf die
      Effektstärke abgebildet. Man kann erkennen, dass es schlicht und einfach
      keinen gibt. Die Effektstärke bleibt konstant über alle Samples.
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/verhältnis-p-wert-effektgröße-stichprobengröße.png"
        alt="verhältnis von p-wert und stichprobengröße"
        class="width-60"
      />
    </div>
    <div class="img-text">
      Abbildung 3: Mittelwertsunterschied, Effektstärke und P-Wert in
      Abhängigkeit der Stichprobengröße (Krahl, 2021)
    </div>
    <p>
      Mit Cohens (1992) Formel lässt sich die Effektstärke wie folgt berechnen:
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/pwert/cohens-formel.png"
        class="width-20"
        alt="cohens formel"
      />
    </div>
    <p>
      Bereits hier wird ersichtlich, dass über die Freiheitsgrade die
      Stichprobengröße einfach wieder herausgerechnet wird. Somit bleibt die
      Effektstärke unbeeinflusst. In unserem Beispiel haben wir erwartungsgemäß
      keinen Effekt, der über die Sample Sizes konstant bleibt (r=.005; Cohen,
      1992). Bei unserer sehr großen Stichprobe von N=110000 würden wir dann die
      sehr widersprüchliche Aussage erhalten, einen signifikanten nicht-Effekt
      gefunden zu haben.
    </p>
    <p>
      Der Vorteil der Effektstärke ist also deutlich. Gerade bei großen
      Stichproben ist es oft kaum möglich nicht-signifikante Ergebnisse zu
      erhalten. Das freut die Autoren sehr, wird aber spätestens dann
      unangenehm, wenn Ergebnisse nicht repliziert werden können.
    </p>
    <p>
      Eine weitere Möglichkeiten für Hypothesentests bzw. die Auswahl oder
      Entscheidung zugunsten Alternativ- oder Nullhypothese bietet die Bayessche
      Statistik.
    </p>
    <h2 id="bayes-faktor">Bayessche Statistik und Bayes Faktor</h2>
    <p>
      Der Bayes Faktor bietet die Möglichkeit Hypothesen gegeneinander zu testen
      bzw. ist ein Instrument zur Favorisierung. Vereinfacht gesagt spiegelt er
      eine Tendenz wider, ob nun Null- oder Alternativhypothesen die empirischen
      Daten besser erklären können. Der große Vorteil der Bayesschen Statistik
      liegt in der Berücksichtigung von Vorinformation (a priori, Prior).
    </p>
    <p>
      Ein sehr anschauliches Beispiel liefert Tschirk (2014) hinsichtlich der
      Zuverlässigkeit klinischer Tests.
    </p>
    <p>
      Nehmen wir einmal an, ein HIV-Test hat eine 99% Sensitivität (+ als +
      erkennen) und eine 99% Spezifität (- als -) erkennen. Wie hoch ist also
      die Wahrscheinlichkeit, dass eine Person aus Österreich tatsächlich das
      HI-Virus hat, wenn der HIV-Test positiv wird? Als weitere Informationen
      steht uns die Punktprävalenz von 0.001zur Verfügung (15000 von 8.4Mio
      haben HIV; Stand 2014). Auch hier hilft uns wieder die Vierfeldertafel:
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/bayes/vierfelder-infektionsgenauigkeit.png"
        class="width-50"
        alt="vierfelder-infektionsgenauigkeit"
      />
    </div>
    <p>
      Die Anzahl der richtig positiv und richtig negativen Fälle muss mit der
      Spezifität und Sensitivität von jeweils 99% verrechnet werden. Die
      Differenzen ergeben die falsch positiven (α-Fehler) und falsch negativen
      (β -Fehler) Fälle:
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/bayes/vierfelder-infektionsgenauigkeit-alpha-beta-ansicht.png"
        class="width-50"
        alt="vierfelder-infektionsgenauigkeit alpha beta ansicht"
      />
    </div>
    <p>
      Daraus ergeben sich die genauen Fallzahlen richtig/ falsch positiv/negativ
      zugeordneter Personen:
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/bayes/vierfelder-infektionsgenauigkeit-ergebnis.png"
        class="width-50"
        alt="vierfelder-infektionsgenauigkeit ergebnis"
      />
    </div>
    <p>
      Und diese setzen wir nach dem Satz von Bayes ins Verhältnis. Also die
      Wahrscheinlichkeit HIV zu haben, wenn der Test positiv wurde (p(H│P)).
      Diese ergibt sich aus der Wahrscheinlichkeit ein positives Ergebnis zu
      erhalten, wenn man HIV hat, multipliziert mit der Wahrscheinlichkeit
      überhaupt HIV zu haben, geteilt durch die Wahrscheinlichkeit überhaupt ein
      positives Testergebnis zu erhalten.
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/bayes/wahrscheinlichkeiten-infektionsgenauigkeit-formel.png"
        class="width-40"
        alt="wahrscheinlichkeiten-infektionsgenauigkeit formel"
      />
    </div>
    <p>
      Wir erhalten das Verhältnis der richtig positiv zugeordneten Erkrankten zu
      der Gesamtzahl der richtig und falsch positiven Tests:
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/bayes/wahrscheinlichkeiten-infektionsgenauigkeit-werte.png"
        class="width-40"
        alt="wahrscheinlichkeiten-infektionsgenauigkeit werte"
      />
    </div>
    <p>
      Und wenn dieses Ergebnis überraschend ist, dann ist das nicht
      verwunderlich, denn es ist alles andere als intuitiv. Die 99%
      Zuverlässigkeit führt hier zu Verwirrung. In der Psychologie spricht man
      von einer Ankerheuristik. Das heißt, dass diese hohe Zahl eine hohe
      Akkuratheit suggeriert, bevor wir eigentlich weitere Informationen haben.
      Denn die Wahrscheinlichkeit in Österreich zum Gegenwärtigen Zeitpunkt HIV
      zu haben liegt bei gerade einmal 0.178%. Und diese Vorinformation wird in
      der Bayesschen Statistik mit einbezogen, was sie um einiges
      Praxistauglicher und realitätsnäher macht.
    </p>
    <p>
      Die Wahrscheinlichkeit als Österreicher mit dem HI-Virus infiziert zu
      sein, wenn man ein positives Testergebnis erhält und außer der Prävalenz,
      Sensitivität und Spezifität keine weiteren Daten verfügbar sind, liegt bei
      15%!
    </p>
    <p>
      Dieses Prinzip eignet sich sehr gut für das Testen empirischer Hypothesen.
      Bereits 1935 von Harold Jeffreys entwickelt, wird der
      <b>Bayes Faktor</b> dabei zunehmend beliebter. Er ergibt sich aus dem
      Verhältnis der Wahrscheinlichkeit für die beobachteten Daten unter der
      Annahme Alternativhypothese und der Wahrscheinlichkeit für die
      beobachteten Daten unter der Annahme Nullhypothese.
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/bayes/nullhypothese.png"
        class="width-30"
        alt="nullhypothese"
      />
    </div>
    <p>
      Ein großer Vorteil des BF10 liegt in seiner Robustheit bei kleinen
      Stichproben. Hier kann er überzeugendere Aussagen liefern. In unserem
      Beispiel sinkt der BF10 anfangs rasch und bleibt dann relativ konstant auf
      einem sehr niedrigen Wert, der einer Favorisierung der Nullhypothese
      entspricht (s. Abb. 3). Jedoch haben wir in diesem stark vereinfachten
      Beispiel keine Aussagen über a priori Annahmen definiert. Zudem haben wir
      die Plausibilität & Informativität der Daten und Hypothesen ignoriert.
      Beides spielt in der Bayessche Statistik eine wichtige Rolle. Wenn man
      genau hinsieht, erkennt man, dass der BF10 auch bei sehr großen
      Zufallsstichproben leicht steigt. Dies kann einfach dadurch erklärt
      werden, dass die a priori Informationen immer weniger und unsere
      empirischen Daten immer mehr gewichtet werden.
    </p>
    <div class="img-container">
      <img
        src="../../assets/img/hypothesen/verhältnis-bf-p-wert-effektgröße-stichprobengröße.png"
        alt="verhältnis bf p-wert effektgröße stichprobengröße"
        class="width-60"
      />
    </div>
    <div class="img-text">
      Abbildung 4: Mittelwertsunterschied, Bayes Faktor, Effektstärke und P-Wert
      in Abhängigkeit der Stichprobengröße (Krahl, 2021)
    </div>
    <p><b>Hanseatic-Statistics (Krahl, 2021)</b></p>
    <br /><br />
    <p>
      Abschließend stellen wir euch noch den Code für unsere kleine Studie zur
      Verfügung
    </p>
    <iframe
      width="100%"
      height="700px"
      src="https://rdrr.io/snippets/embed/?code=library(BayesFactor)%0Alibrary(ggplot2)%0Alibrary(tidyr)%0Alibrary(extrafont)%0A%0A%0Aset.seed(22)%3BIQ_2000%3C-rnorm(100%2C100%2C15)%0Aset.seed(23)%3BIQ_2020%3C-rnorm(100%2C100%2C15)%0A%0A%0A%23100%0A%23ttest%0Ap_100%3C-t.test(IQ_2000%2C%20IQ_2020%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_100%3C-sqrt((t.test(IQ_2000%2C%20IQ_2020%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000%2C%20IQ_2020%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000%2C%20IQ_2020%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_100_1%3C-mean(IQ_2000)%0Amean_100_2%3C-mean(IQ_2020)%0Amean_diff_100%3C-mean_100_1-mean_100_2%0ABF_100%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000%2C%20IQ_2020%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%0A%0A%0A%23200%0A%0AIQ_2000_200%3C-c(IQ_2000%2CIQ_2000)%0AIQ_2020_200%3C-c(IQ_2020%2CIQ_2020)%0A%0Ap_200%3C-t.test(IQ_2000_200%2C%20IQ_2020_200%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_200%3C-sqrt((t.test(IQ_2000_200%2C%20IQ_2020_200%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_200%2C%20IQ_2020_200%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_200%2C%20IQ_2020_200%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_200_1%3C-mean(IQ_2000_200)%0Amean_200_2%3C-mean(IQ_2020_200)%0Amean_diff_200%3C-mean_200_1-mean_200_2%0ABF_200%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_200%2C%20IQ_2020_200%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_200)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%0A%23500%0A%0AIQ_2000_500%3C-c(IQ_2000%2CIQ_2000%2CIQ_2000%2CIQ_2000%2CIQ_2000)%0AIQ_2020_500%3C-c(IQ_2020%2CIQ_2020%2CIQ_2020%2CIQ_2020%2CIQ_2020)%0A%0Ap_500%3C-t.test(IQ_2000_500%2C%20IQ_2020_500%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_500%3C-sqrt((t.test(IQ_2000_500%2C%20IQ_2020_500%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_500%2C%20IQ_2020_500%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_500%2C%20IQ_2020_500%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_500_1%3C-mean(IQ_2000_500)%0Amean_500_2%3C-mean(IQ_2020_500)%0Amean_diff_500%3C-mean_500_1-mean_500_2%0ABF_500%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_500%2C%20IQ_2020_500%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_500)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%0A%231000%0A%0AIQ_2000_1000%3C-c(IQ_2000_500%2CIQ_2000_500)%0AIQ_2020_1000%3C-c(IQ_2020_500%2CIQ_2020_500)%0A%0Ap_1000%3C-t.test(IQ_2000_1000%2C%20IQ_2020_1000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_1000%3C-sqrt((t.test(IQ_2000_1000%2C%20IQ_2020_1000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_1000%2C%20IQ_2020_1000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_1000%2C%20IQ_2020_1000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_1000_1%3C-mean(IQ_2000_1000)%0Amean_1000_2%3C-mean(IQ_2020_1000)%0Amean_diff_1000%3C-mean_1000_1-mean_1000_2%0ABF_1000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_1000%2C%20IQ_2020_1000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_1000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%232000%0A%0AIQ_2000_2000%3C-c(IQ_2000_1000%2CIQ_2000_1000)%0AIQ_2020_2000%3C-c(IQ_2020_1000%2CIQ_2020_1000)%0A%0Ap_2000%3C-t.test(IQ_2000_2000%2C%20IQ_2020_2000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_2000%3C-sqrt((t.test(IQ_2000_2000%2C%20IQ_2020_2000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_2000%2C%20IQ_2020_2000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_2000%2C%20IQ_2020_2000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_2000_1%3C-mean(IQ_2000_2000)%0Amean_2000_2%3C-mean(IQ_2020_2000)%0Amean_diff_2000%3C-mean_2000_1-mean_2000_2%0ABF_2000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_2000%2C%20IQ_2020_2000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_2000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%0A%235000%0A%0AIQ_2000_5000%3C-c(IQ_2000_1000%2CIQ_2000_1000%2CIQ_2000_1000%2CIQ_2000_1000%2CIQ_2000_1000)%0AIQ_2020_5000%3C-c(IQ_2020_1000%2CIQ_2020_1000%2CIQ_2020_1000%2CIQ_2020_1000%2CIQ_2020_1000)%0A%0Ap_5000%3C-t.test(IQ_2000_5000%2C%20IQ_2020_5000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_5000%3C-sqrt((t.test(IQ_2000_5000%2C%20IQ_2020_5000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_5000%2C%20IQ_2020_5000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_5000%2C%20IQ_2020_5000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_5000_1%3C-mean(IQ_2000_5000)%0Amean_5000_2%3C-mean(IQ_2020_5000)%0Amean_diff_5000%3C-mean_5000_1-mean_5000_2%0ABF_5000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_5000%2C%20IQ_2020_5000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_5000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%2310000%0A%0AIQ_2000_10000%3C-c(IQ_2000_5000%2CIQ_2000_5000)%0AIQ_2020_10000%3C-c(IQ_2020_5000%2CIQ_2020_5000)%0A%0Ap_10000%3C-t.test(IQ_2000_10000%2C%20IQ_2020_10000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_10000%3C-sqrt((t.test(IQ_2000_10000%2C%20IQ_2020_10000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_10000%2C%20IQ_2020_10000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_10000%2C%20IQ_2020_10000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_10000_1%3C-mean(IQ_2000_10000)%0Amean_10000_2%3C-mean(IQ_2020_10000)%0Amean_diff_10000%3C-mean_10000_1-mean_10000_2%0ABF_10000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_10000%2C%20IQ_2020_10000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_10000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%2315000%0A%0AIQ_2000_15000%3C-c(IQ_2000_5000%2CIQ_2000_5000%2CIQ_2000_5000)%0AIQ_2020_15000%3C-c(IQ_2020_5000%2CIQ_2020_5000%2CIQ_2020_5000)%0A%0Ap_15000%3C-t.test(IQ_2000_15000%2C%20IQ_2020_15000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_15000%3C-sqrt((t.test(IQ_2000_15000%2C%20IQ_2020_15000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_15000%2C%20IQ_2020_15000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_15000%2C%20IQ_2020_15000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_15000_1%3C-mean(IQ_2000_15000)%0Amean_15000_2%3C-mean(IQ_2020_15000)%0Amean_diff_15000%3C-mean_15000_1-mean_15000_2%0ABF_15000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_15000%2C%20IQ_2020_15000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_15000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%0A%2320000%0A%0AIQ_2000_20000%3C-c(IQ_2000_10000%2CIQ_2000_10000)%0AIQ_2020_20000%3C-c(IQ_2020_10000%2CIQ_2020_10000)%0A%0Ap_20000%3C-t.test(IQ_2000_20000%2C%20IQ_2020_20000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_20000%3C-sqrt((t.test(IQ_2000_20000%2C%20IQ_2020_20000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_20000%2C%20IQ_2020_20000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_20000%2C%20IQ_2020_20000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_20000_1%3C-mean(IQ_2000_20000)%0Amean_20000_2%3C-mean(IQ_2020_20000)%0Amean_diff_20000%3C-mean_20000_1-mean_20000_2%0ABF_20000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_20000%2C%20IQ_2020_20000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_20000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%0A%2325000%0A%0AIQ_2000_25000%3C-c(IQ_2000_15000%2CIQ_2000_10000)%0AIQ_2020_25000%3C-c(IQ_2020_15000%2CIQ_2020_10000)%0A%0Ap_25000%3C-t.test(IQ_2000_25000%2C%20IQ_2020_25000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_25000%3C-sqrt((t.test(IQ_2000_25000%2C%20IQ_2020_25000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_25000%2C%20IQ_2020_25000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_25000%2C%20IQ_2020_25000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_25000_1%3C-mean(IQ_2000_25000)%0Amean_25000_2%3C-mean(IQ_2020_25000)%0Amean_diff_25000%3C-mean_25000_1-mean_25000_2%0ABF_25000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_25000%2C%20IQ_2020_25000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_25000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%2350000%0A%0AIQ_2000_50000%3C-c(IQ_2000_25000%2CIQ_2000_25000)%0AIQ_2020_50000%3C-c(IQ_2020_25000%2CIQ_2020_25000)%0A%0Ap_50000%3C-t.test(IQ_2000_50000%2C%20IQ_2020_50000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_50000%3C-sqrt((t.test(IQ_2000_50000%2C%20IQ_2020_50000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_50000%2C%20IQ_2020_50000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_50000%2C%20IQ_2020_50000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_50000_1%3C-mean(IQ_2000_50000)%0Amean_50000_2%3C-mean(IQ_2020_50000)%0Amean_diff_50000%3C-mean_50000_1-mean_50000_2%0ABF_50000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_50000%2C%20IQ_2020_50000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_50000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%23100000%0A%0AIQ_2000_100000%3C-c(IQ_2000_50000%2CIQ_2000_50000)%0AIQ_2020_100000%3C-c(IQ_2020_50000%2CIQ_2020_50000)%0A%0Ap_100000%3C-t.test(IQ_2000_100000%2C%20IQ_2020_100000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_100000%3C-sqrt((t.test(IQ_2000_100000%2C%20IQ_2020_100000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_100000%2C%20IQ_2020_100000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_100000%2C%20IQ_2020_100000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_100000_1%3C-mean(IQ_2000_100000)%0Amean_100000_2%3C-mean(IQ_2020_100000)%0Amean_diff_100000%3C-mean_100000_1-mean_100000_2%0ABF_100000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_100000%2C%20IQ_2020_100000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_100000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%23150000%0A%0AIQ_2000_150000%3C-c(IQ_2000_100000%2CIQ_2000_50000)%0AIQ_2020_150000%3C-c(IQ_2020_100000%2CIQ_2020_50000)%0A%0Ap_150000%3C-t.test(IQ_2000_150000%2C%20IQ_2020_150000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_150000%3C-sqrt((t.test(IQ_2000_150000%2C%20IQ_2020_150000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_150000%2C%20IQ_2020_150000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_150000%2C%20IQ_2020_150000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_150000_1%3C-mean(IQ_2000_150000)%0Amean_150000_2%3C-mean(IQ_2020_150000)%0Amean_diff_150000%3C-mean_150000_1-mean_150000_2%0ABF_150000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_150000%2C%20IQ_2020_150000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_150000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%0A%0A%23sig%20niveau%0A%0AIQ_2000_110000%3C-c(IQ_2000_100000%2CIQ_2000_10000)%0AIQ_2020_110000%3C-c(IQ_2020_100000%2CIQ_2020_10000)%0A%0Ap_110000%3C-t.test(IQ_2000_110000%2C%20IQ_2020_110000%2C%20paired%20%3D%20TRUE)%5B%5B3%5D%5D%0Aeffectsize_110000%3C-sqrt((t.test(IQ_2000_110000%2C%20IQ_2020_110000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2F((t.test(IQ_2000_110000%2C%20IQ_2020_110000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D**2)%2Bt.test(IQ_2000_110000%2C%20IQ_2020_110000%2C%20paired%20%3D%20TRUE)%5B%5B2%5D%5D))%0Amean_110000_1%3C-mean(IQ_2000_110000)%0Amean_110000_2%3C-mean(IQ_2020_110000)%0Amean_diff_110000%3C-mean_110000_1-mean_110000_2%0ABF_110000%3C-exp(ttest.tstat(t%3Dt.test(IQ_2000_110000%2C%20IQ_2020_110000%2C%20paired%20%3D%20TRUE)%5B%5B1%5D%5D%2C%20n1%3Dlength(IQ_2020_110000)%2C%20rscale%20%3D%200.707)%5B%5B'bf'%5D%5D)%0A%0A%0A%0A%0A%0A%23%23%23all%20together%0A%0Ap_value%3C-c(p_100%2Cp_200%2Cp_500%2Cp_1000%2Cp_2000%2Cp_5000%2Cp_10000%2Cp_15000%2Cp_20000%2Cp_25000%2Cp_50000%2Cp_100000%2Cp_150000)%0Aeffectsize%3C-c(effectsize_100%2Ceffectsize_200%2Ceffectsize_500%2Ceffectsize_1000%2Ceffectsize_2000%2Ceffectsize_5000%2Ceffectsize_10000%2Ceffectsize_15000%2Ceffectsize_20000%2Ceffectsize_25000%2Ceffectsize_50000%2Ceffectsize_100000%2Ceffectsize_150000)%0Asample%3C-c(100%2C200%2C500%2C1000%2C2000%2C5000%2C10000%2C15000%2C20000%2C25000%2C50000%2C100000%2C150000)%0Amean_diff%3C-c(mean_diff_100%2Cmean_diff_200%2Cmean_diff_500%2Cmean_diff_1000%2Cmean_diff_2000%2Cmean_diff_5000%2Cmean_diff_10000%2Cmean_diff_15000%2Cmean_diff_20000%2Cmean_diff_25000%2Cmean_diff_50000%2Cmean_diff_100000%2Cmean_diff_150000)%0Abf%3C-c(BF_100%2CBF_200%2CBF_500%2CBF_1000%2CBF_2000%2CBF_5000%2CBF_10000%2CBF_15000%2CBF_20000%2CBF_25000%2CBF_50000%2CBF_100000%2CBF_150000)%0A%0A%23%23plot%0A%0Adata%3C-data.frame(sample%2Cp_value%2Ceffectsize%2Cmean_diff%2Cbf)%0Adata%24mean_diff%3C-abs(data%24mean_diff)%0A%0A%0A%0A%23%23bf%2Ce%2Cp%0A%0Acolors%20%3C-%20c(%22P%20Value%22%3D%221b2531%22%2C%20%22Effect%20Size%22%3D%22firebrick%22%2C%20%22Mean%20Diff%22%20%3D%20%22cyan%22%2C%20%22Bayes%20Factor%22%20%3D%20%22lightsteelblue%22)%0Aggplot(data%3Ddata%2C%20aes(x%3Dsample))%2B%0A%20%20geom_line(aes(y%20%3D%20p_value%2C%20color%20%3D%20%22P%20Value%22)%2C%20size%3D1.5)%20%2B%20%0A%20%20geom_line(aes(y%20%3D%20effectsize%2C%20color%3D%22Effect%20Size%22)%2C%20size%3D1.5)%20%2B%0A%20%20geom_line(aes(y%20%3D%20mean_diff%2C%20color%3D%22Mean%20Diff%22)%2C%20size%3D1.5)%20%2B%0A%20%20geom_line(aes(y%20%3D%20bf%2C%20color%3D%22Bayes%20Factor%22)%2C%20size%3D1.5)%20%2B%0A%20%20ylim(c(0%2C1))%2B%0A%20%20labs(title%3Dpaste(%22Relationship%20between%20BF%2C%20p-value%2C%20effect%20size%20%26%20sample%20size%22)%2C%20x%3D'Sample%20size'%2C%20y%3D'Value'%2Ccolor%20%3D%20%22Legend%22)%2B%0A%20%20scale_color_manual(values%20%3D%20colors)%2B%0A%20%20theme(text%3Delement_text(family%3D%22Times%20New%20Roman%22%2C%20face%3D%22bold%22%2C%20size%3D20))"
      frameborder="0"
    ></iframe>
  </div>
</div>
